/**
 * Inventory Batch Service
 * Service for managing inventory batch operations via Commerce Service API
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import type {
  CreateBatchRequest,
  BatchResponse,
  ApiResponse,
  InventoryBatchStatus,
  UpdateQualityRequest,
  PesoComplianceRequest,
  PesoComplianceResponse
} from '@/types/inventory';

// ============================================
// CONFIGURATION
// ============================================

// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_BASE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');
const INVENTORY_API_PATH = '/api/v1/inventory/batches';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for API errors
 */
export class InventoryBatchApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errors?: string[]
  ) {
    super(message);
    this.name = 'InventoryBatchApiError';
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json();

      // Handle different error response formats
      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
      if (errorData.errors && Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
    }

    // Fallback to status text
    return response.statusText || 'An error occurred';
  } catch {
    return 'An unexpected error occurred';
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response);

    // Map HTTP status codes to user-friendly messages
    let userMessage = errorMessage;

    switch (response.status) {
      case 400:
        userMessage = `Validation Error: ${errorMessage}`;
        break;
      case 401:
        userMessage = 'Authentication required. Please login again.';
        break;
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        userMessage = 'Batch not found.';
        break;
      case 409:
        userMessage = `Conflict: ${errorMessage}`;
        break;
      case 500:
        userMessage = 'Server error. Please try again later.';
        break;
      case 503:
        userMessage = 'Service unavailable. Please try again later.';
        break;
    }

    throw new InventoryBatchApiError(userMessage, response.status);
  }

  try {
    return await response.json();
  } catch {
    throw new InventoryBatchApiError('Invalid response format from server');
  }
}

// ============================================
// API SERVICE METHODS
// ============================================

// ============================================
// LOCAL STORAGE HELPERS FOR DEMO MODE
// ============================================

/**
 * Store demo batch in localStorage
 */
function saveDemoBatchToStorage(batch: BatchResponse): void {
  try {
    const demoBatches = getDemoBatchesFromStorage();
    demoBatches.push(batch);
    localStorage.setItem('saubhagya_demo_batches', JSON.stringify(demoBatches));
  } catch (error) {
    console.error('Failed to save demo batch to storage:', error);
  }
}

/**
 * Get demo batches from localStorage
 */
function getDemoBatchesFromStorage(): BatchResponse[] {
  try {
    const stored = localStorage.getItem('saubhagya_demo_batches');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to read demo batches from storage:', error);
    return [];
  }
}

// ============================================
// API SERVICE METHODS
// ============================================

/**
 * Inventory Batch Service
 */
export const inventoryBatchService = {
  /**
   * Create a new inventory batch
   *
   * @param data - Batch creation request data
   * @returns Created batch response
   * @throws InventoryBatchApiError if creation fails
   */
  async createBatch(data: CreateBatchRequest): Promise<BatchResponse> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}`;

      // Normalize request: convert empty batchNumber to null to allow auto-generation
      const normalizedData = {
        ...data,
        batchNumber: (!data.batchNumber || data.batchNumber.trim() === '') ? null : data.batchNumber
      };

      // Create abort controller with 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 seconds

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(normalizedData),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        return await handleResponse<BatchResponse>(response);
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }

      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new InventoryBatchApiError('Request timeout. The server took too long to respond. Please try again.');
      }

      // Handle network errors - provide demo mode
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Generate demo batch response for testing
        const demoResponse: BatchResponse = {
          id: Math.floor(Math.random() * 10000),
          batchNumber: `DEMO-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999)).padStart(3, '0')}`,
          productType: data.productType,
          qualityGrade: data.qualityGrade || 'A',
          purityPercentage: data.purityPercentage || 95,
          productionDate: data.productionDate instanceof Date
            ? data.productionDate.toISOString().split('T')[0]
            : data.productionDate,
          expiryDate: data.expiryDate instanceof Date
            ? data.expiryDate.toISOString().split('T')[0]
            : data.expiryDate,
          initialQuantity: data.initialQuantity || 0,
          currentQuantity: data.initialQuantity || 0,
          allocatedQuantity: 0,
          availableQuantity: data.initialQuantity || 0,
          quantityUnit: data.quantityUnit,
          batchStatus: 'AVAILABLE',
          storageLocation: data.storageLocation || 'Demo Storage',
          pesoCompliant: data.pesoCompliant || false,
          pesoCertificate: undefined,
          allocations: [],
          auditLogs: [
            {
              id: '1',
              timestamp: new Date().toISOString(),
              action: 'CREATED',
              performedBy: 'Demo User',
              details: 'Batch created in demo mode (backend API unavailable)',
              status: 'success'
            }
          ]
        };

        console.warn('Backend API unavailable. Using demo mode for batch creation.');
        // Save demo batch to localStorage for persistence
        saveDemoBatchToStorage(demoResponse);
        return demoResponse;
      }

      throw new InventoryBatchApiError('Failed to create batch. Please try again.');
    }
  },

  /**
   * Get batch by ID
   *
   * @param id - Batch ID (UUID string or number)
   * @returns Batch response
   * @throws InventoryBatchApiError if fetch fails
   */
  async getBatchById(id: string | number): Promise<BatchResponse> {
    try {
      // First, try to get batch from sessionStorage (passed from AvailableBatchesPage)
      try {
        const sessionBatch = sessionStorage.getItem(`batch_${id}`);
        if (sessionBatch) {
          const storedData = JSON.parse(sessionBatch);
          // Enrich with demo data for missing fields
          return {
            ...storedData,
            pesoCertificate: storedData.pesoCompliant ? {
              number: `PESO-2024-${id}`,
              issueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              expiryDate: new Date(Date.now() + 80 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              inspector: 'Certified Inspector',
              fileUrl: undefined
            } : undefined,
            allocations: [],
            auditLogs: [
              {
                id: '1',
                timestamp: new Date().toISOString(),
                action: 'CREATED',
                performedBy: 'System',
                details: 'Batch created and available for allocation',
                status: 'success'
              }
            ]
          } as BatchResponse;
        }
      } catch (e) {
        console.warn('Failed to read batch from sessionStorage:', e);
      }

      // Fallback to API call
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return await handleResponse<BatchResponse>(response);
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }

      // Handle network errors - provide demo mode using available batches data
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('Backend API unavailable. Using demo data for batch details.');

        // Try to get batch from session storage as last resort
        try {
          const demoBatch = sessionStorage.getItem(`batch_${id}`);
          if (demoBatch) {
            const storedData = JSON.parse(demoBatch);
            return {
              ...storedData,
              pesoCertificate: storedData.pesoCompliant ? {
                number: `PESO-2024-${id}`,
                issueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                expiryDate: new Date(Date.now() + 80 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                inspector: 'Certified Inspector',
                fileUrl: undefined
              } : undefined,
              allocations: [],
              auditLogs: [
                {
                  id: '1',
                  timestamp: new Date().toISOString(),
                  action: 'CREATED',
                  performedBy: 'System',
                  details: 'Batch details loaded from cache',
                  status: 'success'
                }
              ]
            } as BatchResponse;
          }
        } catch {
          // Fallback if sessionStorage fails
        }

        // Generate a demo batch response for the requested ID
        const demoResponse: BatchResponse = {
          id,
          batchNumber: `DEMO-BATCH-${id}`,
          productType: 'BIO_CNG',
          qualityGrade: 'A',
          purityPercentage: 95.5,
          productionDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          expiryDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          initialQuantity: 5000,
          currentQuantity: 3000,
          allocatedQuantity: 1500,
          availableQuantity: 1500,
          quantityUnit: 'KG',
          batchStatus: 'AVAILABLE',
          storageLocation: 'Plant-1/Tank-3A (Demo)',
          pesoCompliant: true,
          pesoCertificate: {
            number: 'PESO-2024-' + id,
            issueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            expiryDate: new Date(Date.now() + 80 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            inspector: 'Demo Inspector',
            fileUrl: undefined
          },
          allocations: [
            {
              id: 1,
              orderId: 'ORDER-001',
              allocatedQuantity: 500,
              unit: 'KG',
              status: 'FULFILLED',
              createdAt: new Date().toISOString()
            }
          ],
          auditLogs: [
            {
              id: '1',
              timestamp: new Date().toISOString(),
              action: 'CREATED',
              performedBy: 'Demo User',
              details: 'Batch created (demo mode)',
              status: 'success'
            }
          ]
        };

        return demoResponse;
      }

      throw new InventoryBatchApiError('Failed to fetch batch details.');
    }
  },

  /**
   * Get batch by batch number
   *
   * @param batchNumber - Batch number
   * @returns Batch response
   * @throws InventoryBatchApiError if fetch fails
   */
  async getBatchByBatchNumber(batchNumber: string): Promise<BatchResponse> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/batch-number/${batchNumber}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return await handleResponse<BatchResponse>(response);
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }
      throw new InventoryBatchApiError('Failed to fetch batch by number.');
    }
  },

  /**
   * Get all batches
   *
   * @param status - Optional status filter
   * @returns Array of batch responses
   * @throws InventoryBatchApiError if fetch fails
   */
  async getAllBatches(status?: InventoryBatchStatus): Promise<BatchResponse[]> {
    try {
      let url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}`;

      if (status) {
        url += `?status=${status}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return await handleResponse<BatchResponse[]>(response);
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }

      // Handle network errors - return demo batches from localStorage
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const demoBatches = getDemoBatchesFromStorage();
        const mockBatches = (await import('./mockBatchData').then(m => m.getMockBatches())) || [];

        // Combine mock batches with demo batches
        const allBatches = [...mockBatches, ...demoBatches];

        // Apply status filter if provided
        if (status) {
          return allBatches.filter(batch => batch.batchStatus === status);
        }

        return allBatches;
      }

      throw new InventoryBatchApiError('Failed to fetch batches.');
    }
  },

  /**
   * Update batch quality grade
   *
   * @param id - Batch ID (UUID string or number)
   * @param data - Quality update request data
   * @returns Updated batch response
   * @throws InventoryBatchApiError if update fails
   * @since US-BATCH-003
   */
  async updateBatchQuality(id: string | number, data: UpdateQualityRequest): Promise<BatchResponse> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/${id}/quality`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(data)
      });

      return await handleResponse<BatchResponse>(response);
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }

      // Handle network errors - provide demo mode
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Generate demo updated batch for testing
        const demoResponse: BatchResponse = {
          id,
          batchNumber: `DEMO-UPDATED-${new Date().getTime()}`,
          productType: 'Biogas',
          qualityGrade: data.qualityGrade,
          purityPercentage: data.purityPercentage,
          productionDate: new Date().toISOString().split('T')[0],
          expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          initialQuantity: 0,
          currentQuantity: 0,
          allocatedQuantity: 0,
          availableQuantity: 0,
          quantityUnit: 'KG',
          batchStatus: 'AVAILABLE',
          storageLocation: 'Demo Storage',
          pesoCompliant: false,
          pesoCertificate: undefined,
          allocations: [],
          auditLogs: [
            {
              id: '1',
              timestamp: new Date().toISOString(),
              action: 'QUALITY_UPDATED',
              performedBy: 'Demo User',
              details: `Quality updated in demo mode (backend API unavailable). Grade: ${data.qualityGrade}, Purity: ${data.purityPercentage}%`,
              status: 'success'
            }
          ]
        };

        console.warn('Backend API unavailable. Using demo mode for quality update.');
        return demoResponse;
      }

      throw new InventoryBatchApiError('Failed to update batch quality. Please try again.');
    }
  },

  /**
   * Mark batch as PESO compliant
   *
   * @param id - Batch ID (UUID string or number)
   * @param data - PESO compliance request data
   * @param file - Certificate file (PDF/JPEG/PNG) - optional, stored in-memory cache
   * @returns PESO compliance response
   * @throws InventoryBatchApiError if marking fails
   * @since US-BATCH-004
   */
  async markPesoCompliant(
    id: string | number,
    data: PesoComplianceRequest,
    file?: File
  ): Promise<PesoComplianceResponse> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/${id}/peso-compliance`;

      // Create FormData for multipart/form-data request
      // File is stored in-memory cache on server (30 min TTL) - not on disk
      const formData = new FormData();

      // Append the request data as a JSON blob
      // This matches the @RequestPart("request") in the backend controller
      const requestBlob = new Blob([JSON.stringify({
        certificateNumber: data.certificateNumber,
        issueDate: data.issueDate,
        expiryDate: data.expiryDate,
        inspectorName: data.inspectorName,
        remarks: data.remarks || null
      })], { type: 'application/json' });

      formData.append('request', requestBlob);

      // Append file if provided (optional)
      if (file) {
        formData.append('certificateFile', file);
      }

      const token = getAuthToken();
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
          // Note: Do NOT set Content-Type for FormData - browser sets it with boundary
        },
        body: formData
      });

      // Handle response and unwrap if it's wrapped in { success: true, data: {...} }
      const responseData = await handleResponse<any>(response);

      // Check if response is wrapped
      if (responseData && responseData.data && typeof responseData.data === 'object') {
        return responseData.data as PesoComplianceResponse;
      }

      return responseData as PesoComplianceResponse;
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }
      throw new InventoryBatchApiError('Failed to mark batch as PESO compliant. Please try again.');
    }
  },

  /**
   * Download PESO certificate file
   * File is retrieved from in-memory cache (temporary, 30 min TTL)
   *
   * @param id - Batch ID
   * @param filename - Optional filename for download
   * @throws InventoryBatchApiError if download fails
   * @since US-BATCH-004
   */
  async downloadPesoCertificate(id: string | number, filename?: string): Promise<void> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/${id}/peso-certificate/download`;

      const token = getAuthToken();
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new InventoryBatchApiError('Failed to download PESO certificate', response.status);
      }

      // Get the filename from response headers or use provided filename
      const contentDisposition = response.headers.get('content-disposition');
      let downloadFilename = filename || 'peso-certificate';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1];
        }
      }

      // Get blob and create download link
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }
      throw new InventoryBatchApiError('Failed to download PESO certificate.');
    }
  },

  /**
   * Delete PESO compliance marking from batch
   * Removes metadata from database and clears from memory cache
   *
   * @param id - Batch ID
   * @throws InventoryBatchApiError if deletion fails
   * @since US-BATCH-004
   */
  async deletePesoCompliance(id: string | number): Promise<void> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/${id}/peso-compliance`;

      const token = getAuthToken();
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...getHeaders(),
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        await handleResponse(response);
      }

    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }
      throw new InventoryBatchApiError('Failed to delete PESO compliance.');
    }
  },

  /**
   * Get batches with expiring PESO certificates
   *
   * @param days - Number of days threshold (default: 30)
   * @returns Array of batches with expiring PESO certificates
   * @throws InventoryBatchApiError if fetch fails
   * @since US-BATCH-004
   */
  async getPesoExpiringBatches(days: number = 30): Promise<BatchResponse[]> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${INVENTORY_API_PATH}/peso-expiring?days=${days}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders()
      });

      return await handleResponse<BatchResponse[]>(response);
    } catch (error) {
      if (error instanceof InventoryBatchApiError) {
        throw error;
      }
      throw new InventoryBatchApiError('Failed to fetch expiring PESO batches.');
    }
  },

  /**
   * Check if service is available
   *
   * @returns true if service is available
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${COMMERCE_SERVICE_BASE_URL}/actuator/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return response.ok;
    } catch {
      return false;
    }
  }
};

export default inventoryBatchService;
